import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Upload,
  Star,
  ChevronUp,
  ChevronDown,
  Save,
  X,
  Image as ImageIcon,
} from "lucide-react";

const KIND_LABELS = {
  text: "Texte",
  gallery: "Galerie",
  team: "Équipe / Personnes",
  cards: "Cartes",
};
const KIND_ORDER = ["text", "gallery", "team", "cards"];

/* -------------------- Image Uploader -------------------- */
function ImageUploader({ multiple = true, onUploaded, testid = "uploader" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (onUploaded) await onUploaded(r.data.url);
      }
      toast.success(`${files.length} photo(s) importée(s)`);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Import échoué");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handle}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="border border-[#0A1128]/30 text-[#0A1128] px-4 py-2 text-xs tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-2 disabled:opacity-60"
        data-testid={`${testid}-button`}
      >
        <Upload size={14} /> {uploading ? "..." : (multiple ? "Importer des photos" : "Importer")}
      </button>
    </>
  );
}

/* -------------------- Item Editor (team / cards) -------------------- */
function ItemEditor({ item, onSave, onCancel }) {
  const [form, setForm] = useState({ ...item });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="border border-[#D4AF37]/40 bg-[#F9F8F6] p-5 space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        <input placeholder="Prénom / Nom" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="item-name" />
        <input placeholder="Rôle (FR)" value={form.role_fr || ""} onChange={(e) => set("role_fr", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="item-role-fr" />
        <input placeholder="Rôle (EN)" value={form.role_en || ""} onChange={(e) => set("role_en", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="item-role-en" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <textarea placeholder="Texte de présentation (FR)" rows={4} value={form.body_fr || ""} onChange={(e) => set("body_fr", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" data-testid="item-body-fr" />
        <textarea placeholder="Texte de présentation (EN)" rows={4} value={form.body_en || ""} onChange={(e) => set("body_en", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" data-testid="item-body-en" />
      </div>
      <div className="flex items-start gap-4">
        <div className="w-32 h-32 bg-[#0A1128]/5 border border-[#0A1128]/10 overflow-hidden flex items-center justify-center">
          {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={24} className="text-[#0A1128]/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <input placeholder="URL de la photo" value={form.image || ""} onChange={(e) => set("image", e.target.value)} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="item-image-url" />
          <ImageUploader multiple={false} onUploaded={(url) => set("image", url)} testid="item-image" />
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={() => onSave(form)} className="bg-[#0A1128] text-[#F9F8F6] px-6 py-2 text-xs tracking-[0.24em] uppercase flex items-center gap-2" data-testid="item-save">
          <Save size={14} /> Enregistrer
        </button>
        <button onClick={onCancel} className="text-xs tracking-[0.24em] uppercase text-[#0A1128]/60 hover:text-[#0A1128] flex items-center gap-2" data-testid="item-cancel">
          <X size={14} /> Annuler
        </button>
      </div>
    </div>
  );
}

/* -------------------- Single Category Panel -------------------- */
function CategoryPanel({ cat, index, total, onChange, onMoveUp, onMoveDown, onDelete }) {
  const [meta, setMeta] = useState({
    slug: cat.slug,
    kind: cat.kind,
    title_fr: cat.title_fr,
    title_en: cat.title_en,
    intro_fr: cat.intro_fr,
    intro_en: cat.intro_en,
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [creatingItem, setCreatingItem] = useState(false);

  const metaDirty =
    meta.slug !== cat.slug ||
    meta.kind !== cat.kind ||
    meta.title_fr !== cat.title_fr ||
    meta.title_en !== cat.title_en ||
    meta.intro_fr !== cat.intro_fr ||
    meta.intro_en !== cat.intro_en;

  const saveMeta = async () => {
    try {
      await api.patch(`/admin/categories/${cat.cat_id}`, meta);
      toast.success("Catégorie mise à jour");
      onChange();
    } catch {
      toast.error("Échec");
    }
  };

  const addImage = async (url) => {
    await api.post(`/admin/categories/${cat.cat_id}/images`, { url, is_primary: false });
    onChange();
  };

  const setPrimary = async (img_id) => {
    await api.patch(`/admin/categories/${cat.cat_id}/images/${img_id}`, { is_primary: true });
    toast.success("Image principale mise à jour");
    onChange();
  };

  const removeImage = async (img_id) => {
    await api.delete(`/admin/categories/${cat.cat_id}/images/${img_id}`);
    onChange();
  };

  const saveItem = async (data) => {
    try {
      const payload = {
        name: data.name || "",
        role_fr: data.role_fr || "",
        role_en: data.role_en || "",
        body_fr: data.body_fr || "",
        body_en: data.body_en || "",
        image: data.image || "",
      };
      if (data.item_id) {
        await api.patch(`/admin/categories/${cat.cat_id}/items/${data.item_id}`, payload);
      } else {
        await api.post(`/admin/categories/${cat.cat_id}/items`, payload);
      }
      toast.success("Enregistré");
      setEditingItemId(null);
      setCreatingItem(false);
      onChange();
    } catch {
      toast.error("Échec");
    }
  };

  const deleteItem = async (item_id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    await api.delete(`/admin/categories/${cat.cat_id}/items/${item_id}`);
    onChange();
  };

  const moveItem = async (item_id, dir) => {
    const items = [...(cat.items || [])].sort((a, b) => a.order - b.order);
    const idx = items.findIndex((i) => i.item_id === item_id);
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target], items[idx]];
    await api.post(`/admin/categories/${cat.cat_id}/items/reorder`, { order: items.map((i) => i.item_id) });
    onChange();
  };

  const images = [...(cat.images || [])].sort((a, b) => a.order - b.order);
  const items = [...(cat.items || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="bg-white border border-[#0A1128]/10" data-testid={`category-${cat.cat_id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#0A1128]/10 bg-[#0A1128]/[0.02]">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <button onClick={onMoveUp} disabled={index === 0} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30" data-testid={`cat-up-${cat.cat_id}`}><ChevronUp size={16} /></button>
            <button onClick={onMoveDown} disabled={index === total - 1} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30" data-testid={`cat-down-${cat.cat_id}`}><ChevronDown size={16} /></button>
          </div>
          <div>
            <div className="overline text-[#D4AF37]">{KIND_LABELS[cat.kind] || cat.kind}</div>
            <div className="font-serif text-2xl text-[#0A1128]">{cat.title_fr || cat.slug || "(sans titre)"}</div>
            <div className="text-xs text-[#0A1128]/40">/{cat.slug}</div>
          </div>
        </div>
        <button onClick={onDelete} className="text-[#E2856E] hover:text-[#0A1128]" data-testid={`cat-delete-${cat.cat_id}`}>
          <Trash2 size={18} />
        </button>
      </div>

      {/* Meta form */}
      <div className="p-6 space-y-4 border-b border-[#0A1128]/10">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Identifiant (slug)</label>
            <input value={meta.slug} onChange={(e) => setMeta({ ...meta, slug: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`cat-slug-${cat.cat_id}`} />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Type</label>
            <select value={meta.kind} onChange={(e) => setMeta({ ...meta, kind: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1 bg-transparent">
              {KIND_ORDER.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            {metaDirty && (
              <button onClick={saveMeta} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2 text-xs tracking-[0.24em] uppercase flex items-center gap-2" data-testid={`cat-save-meta-${cat.cat_id}`}>
                <Save size={14} /> Enregistrer
              </button>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Titre (FR)</label>
            <input value={meta.title_fr} onChange={(e) => setMeta({ ...meta, title_fr: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`cat-title-fr-${cat.cat_id}`} />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Titre (EN)</label>
            <input value={meta.title_en} onChange={(e) => setMeta({ ...meta, title_en: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Introduction (FR)</label>
            <textarea rows={3} value={meta.intro_fr} onChange={(e) => setMeta({ ...meta, intro_fr: e.target.value })} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm resize-none mt-1" />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Introduction (EN)</label>
            <textarea rows={3} value={meta.intro_en} onChange={(e) => setMeta({ ...meta, intro_en: e.target.value })} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm resize-none mt-1" />
          </div>
        </div>
      </div>

      {/* Images */}
      <div className="p-6 border-b border-[#0A1128]/10">
        <div className="flex items-center justify-between mb-4">
          <div className="overline text-[#D4AF37]">Photos ({images.length})</div>
          <ImageUploader multiple={true} onUploaded={addImage} testid={`cat-images-${cat.cat_id}`} />
        </div>
        {images.length === 0 && <div className="text-xs text-[#0A1128]/40 italic">Aucune photo importée.</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.img_id} className={`relative group border ${img.is_primary ? "border-[#D4AF37]" : "border-[#0A1128]/10"}`} data-testid={`cat-image-${img.img_id}`}>
              <img src={img.url} alt="" className="w-full h-32 object-cover" />
              {img.is_primary && (
                <div className="absolute top-2 left-2 bg-[#D4AF37] text-[#0A1128] px-2 py-0.5 text-[10px] tracking-wide uppercase font-medium flex items-center gap-1"><Star size={10} /> Principale</div>
              )}
              <div className="absolute inset-0 bg-[#0A1128]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                {!img.is_primary && (
                  <button onClick={() => setPrimary(img.img_id)} className="text-white text-xs tracking-wide uppercase flex items-center gap-1" data-testid={`cat-image-primary-${img.img_id}`}>
                    <Star size={14} /> Principale
                  </button>
                )}
                <button onClick={() => removeImage(img.img_id)} className="text-[#F0C85A] hover:text-white" data-testid={`cat-image-delete-${img.img_id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Items */}
      {(cat.kind === "team" || cat.kind === "cards") && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-[#D4AF37]">
              {cat.kind === "team" ? `Personnes (${items.length})` : `Éléments (${items.length})`}
            </div>
            {!creatingItem && (
              <button onClick={() => { setCreatingItem(true); setEditingItemId(null); }} className="bg-[#0A1128] text-[#F9F8F6] px-4 py-2 text-xs tracking-[0.24em] uppercase flex items-center gap-2" data-testid={`cat-item-new-${cat.cat_id}`}>
                <Plus size={14} /> Ajouter
              </button>
            )}
          </div>
          {creatingItem && (
            <div className="mb-4">
              <ItemEditor
                item={{ name: "", role_fr: "", role_en: "", body_fr: "", body_en: "", image: "" }}
                onSave={(d) => saveItem(d)}
                onCancel={() => setCreatingItem(false)}
              />
            </div>
          )}
          <div className="space-y-3">
            {items.map((it, idx) => {
              const isEditing = editingItemId === it.item_id;
              if (isEditing) {
                return (
                  <ItemEditor
                    key={it.item_id}
                    item={it}
                    onSave={(d) => saveItem({ ...d, item_id: it.item_id })}
                    onCancel={() => setEditingItemId(null)}
                  />
                );
              }
              return (
                <div key={it.item_id} className="flex items-center gap-4 border border-[#0A1128]/10 p-3" data-testid={`cat-item-${it.item_id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => moveItem(it.item_id, -1)} disabled={idx === 0} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button onClick={() => moveItem(it.item_id, 1)} disabled={idx === items.length - 1} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30"><ChevronDown size={14} /></button>
                  </div>
                  <div className="w-16 h-16 bg-[#0A1128]/5 border border-[#0A1128]/10 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-[#0A1128]/30" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-[#0A1128] truncate">{it.name || "(sans nom)"}</div>
                    <div className="text-xs text-[#D4AF37]">{it.role_fr}</div>
                    <div className="text-xs text-[#0A1128]/60 line-clamp-1">{it.body_fr}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => { setEditingItemId(it.item_id); setCreatingItem(false); }} className="text-xs tracking-[0.2em] uppercase text-[#0A1128] hover:text-[#D4AF37]" data-testid={`cat-item-edit-${it.item_id}`}>Modifier</button>
                    <button onClick={() => deleteItem(it.item_id)} className="text-[#E2856E] hover:text-[#0A1128]" data-testid={`cat-item-delete-${it.item_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
            {items.length === 0 && !creatingItem && (
              <div className="text-xs text-[#0A1128]/40 italic">
                {cat.kind === "team" ? "Aucune personne. Cliquez sur « Ajouter »." : "Aucun élément. Cliquez sur « Ajouter »."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Categories Manager (root) -------------------- */
export default function CategoriesManager({ onSitePublicRefresh }) {
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState({ slug: "", kind: "team", title_fr: "", title_en: "" });

  const load = async () => {
    try {
      const r = await api.get("/admin/categories");
      setCats(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const refreshAll = async () => {
    await load();
    if (onSitePublicRefresh) onSitePublicRefresh();
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", newCat);
      toast.success("Catégorie créée");
      setNewCat({ slug: "", kind: "team", title_fr: "", title_en: "" });
      setCreating(false);
      refreshAll();
    } catch {
      toast.error("Échec");
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie « ${cat.title_fr || cat.slug} » ? Tous ses contenus seront perdus.`)) return;
    await api.delete(`/admin/categories/${cat.cat_id}`);
    toast.success("Catégorie supprimée");
    refreshAll();
  };

  const move = async (index, dir) => {
    const arr = [...cats];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    await api.post(`/admin/categories/reorder`, { order: arr.map((c) => c.cat_id) });
    refreshAll();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-[#0A1128]">Catégories du site</h2>
          <p className="text-sm text-[#0A1128]/60 mt-1">Créez, modifiez, réorganisez vos catégories et leurs contenus.</p>
        </div>
        {!creating && (
          <button onClick={() => setCreating(true)} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2.5 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors flex items-center gap-2" data-testid="cat-new">
            <Plus size={14} /> Nouvelle catégorie
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[#D4AF37]/40 p-6 grid md:grid-cols-4 gap-3" data-testid="cat-new-form">
          <input required placeholder="Slug (ex: about, guests)" value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="cat-new-slug" />
          <select value={newCat.kind} onChange={(e) => setNewCat({ ...newCat, kind: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm">
            {KIND_ORDER.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>
          <input placeholder="Titre (FR)" value={newCat.title_fr} onChange={(e) => setNewCat({ ...newCat, title_fr: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm md:col-span-2" data-testid="cat-new-title" />
          <div className="md:col-span-4 flex gap-3">
            <button type="submit" className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2 text-xs tracking-[0.24em] uppercase" data-testid="cat-new-submit">Créer</button>
            <button type="button" onClick={() => setCreating(false)} className="text-xs tracking-[0.24em] uppercase text-[#0A1128]/60 hover:text-[#0A1128]">Annuler</button>
          </div>
        </form>
      )}

      {cats.length === 0 && !creating && <div className="text-[#0A1128]/50 italic">Aucune catégorie. Créez la première ci-dessus.</div>}

      {cats.map((cat, idx) => (
        <CategoryPanel
          key={cat.cat_id}
          cat={cat}
          index={idx}
          total={cats.length}
          onChange={refreshAll}
          onMoveUp={() => move(idx, -1)}
          onMoveDown={() => move(idx, 1)}
          onDelete={() => remove(cat)}
        />
      ))}
    </div>
  );
}
